import React, { useState } from 'react';
import AttachmentLightbox, { type AttachmentKind } from './AttachmentLightbox';
import FileTypeIcon from './FileTypeIcon';
import { useChatRoomAttachments } from '../../hooks/chat/useChatRoomAttachments';
import type { ChatAttachment } from '../../types/chat';

function detectKind(mime: string | undefined, filename: string | undefined): AttachmentKind {
  const m = mime ?? '';
  const n = filename ?? '';
  if (m.startsWith('image/') || /\.(png|jpe?g|gif|webp|bmp|svg)$/i.test(n)) return 'image';
  if (m === 'application/pdf' || /\.pdf$/i.test(n)) return 'pdf';
  if (m.startsWith('video/') || /\.(mp4|webm|mov)$/i.test(n)) return 'video';
  return 'other';
}

interface AttachmentItemProps {
  data?: ChatAttachment;
  onOpen: (data: ChatAttachment, kind: AttachmentKind) => void;
}

const AttachmentItem: React.FC<AttachmentItemProps> = ({ data, onOpen }) => {
  if (!data || !data.url) {
    return (
      <div className="flex items-center gap-2 px-2.5 py-1.5 max-w-[260px] rounded-lg border border-border-secondary">
        <FileTypeIcon className="w-7 h-7 shrink-0" />
        <p className="truncate text-xs text-text-tertiary">Loading…</p>
      </div>
    );
  }

  const kind = detectKind(data.mime_type, data.original_name);

  if (kind === 'image') {
    return (
      <button
        type="button"
        onClick={() => onOpen(data, kind)}
        className="block rounded-full overflow-hidden border border-border-secondary focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
      >
        <img
          src={data.url}
          alt={data.original_name}
          className="max-h-72 max-w-full object-cover"
          loading="lazy"
        />
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={() => onOpen(data, kind)}
      className="text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 rounded-full"
    >
      <div className="flex items-center gap-2 px-2.5 py-1.5 max-w-[260px] rounded-lg border border-border-secondary hover:border-border-primary transition-colors">
        <FileTypeIcon
          mime={data.mime_type}
          filename={data.original_name}
          className="w-7 h-7 shrink-0"
        />
        <div className="flex-1 min-w-0">
          <p className="truncate text-xs font-medium text-text-primary">{data.original_name}</p>
          <p className="text-[11px] text-text-tertiary">
            {kind === 'pdf' ? 'PDF' : kind === 'video' ? 'Video' : 'File'}
          </p>
        </div>
      </div>
    </button>
  );
};

interface MessageAttachmentsProps {
  roomId: string;
  attachmentIds: string[];
}

const MessageAttachments: React.FC<MessageAttachmentsProps> = ({
  roomId,
  attachmentIds,
}) => {
  const { map } = useChatRoomAttachments(roomId);
  const [viewer, setViewer] = useState<{ data: ChatAttachment; kind: AttachmentKind } | null>(
    null
  );

  if (!attachmentIds.length) return null;

  return (
    <>
      <div className="flex flex-col gap-2">
        {attachmentIds.map((id) => (
          <AttachmentItem
            key={id}
            data={map.get(id)}
            onOpen={(data, kind) => setViewer({ data, kind })}
          />
        ))}
      </div>
      {viewer && viewer.data.url && (
        <AttachmentLightbox
          url={viewer.data.url}
          kind={viewer.kind}
          filename={viewer.data.original_name}
          mime={viewer.data.mime_type}
          onClose={() => setViewer(null)}
        />
      )}
    </>
  );
};

export default MessageAttachments;
